import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAdsForTopics } from "../lib/api";
import { loadIdentity } from "../lib/identity";

// Picks a random element different from `excludeId`. Returns `null` when none.
function pickAlt(pool, excludeId) {
  const alts = pool.filter((a) => a?.id !== excludeId);
  if (!alts.length) return null;
  return alts[Math.floor(Math.random() * alts.length)];
}

// Manages the pool of ad campaigns + hero/feed slots. All setters are stable
// and the pool lives in a ref so callbacks stay referentially stable.
export function useAdsData() {
  const poolRef = useRef([]);
  const [heroAd, setHeroAd] = useState(null);
  const [feedAds, setFeedAds] = useState([]);
  const [premiumNoAds, setPremiumNoAds] = useState(false);
  const [adsEntitlementReady, setAdsEntitlementReady] = useState(false);


  useEffect(() => {
    let cancelled = false;

    const loadAdsEntitlement = async () => {
      try {
        const identity = loadIdentity();
        const identityId = identity?.identityId;

        if (!identityId) {
          if (!cancelled) {
            setPremiumNoAds(false);
          }
          return;
        }

        const response = await fetch(
          "/api/v1/payments/entitlement",
          {
            method: "GET",
            credentials: "include",
            headers: {
              "x-ol-identity": identityId,
            },
          }
        );

        const payload = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.message ||
            payload?.error ||
            "Impossibile leggere lo stato Premium."
          );
        }

        const plansWithoutAds = new Set([
          "premium_start",
          "premium_plus",
          "creator_pro",
        ]);

        const noAds =
          Boolean(payload?.active) &&
          plansWithoutAds.has(payload?.plan);

        if (!cancelled) {
          setPremiumNoAds(noAds);
        }
      } catch (error) {
        console.error(
          "useAdsData: entitlement Premium non disponibile",
          error
        );

        if (!cancelled) {
          setPremiumNoAds(false);
        }
      } finally {
        if (!cancelled) {
          setAdsEntitlementReady(true);
        }
      }
    };

    void loadAdsEntitlement();

    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    fetchAdsForTopics()
      .then(({ pool }) => {
        if (cancelled) return;
        poolRef.current = pool;
        setHeroAd(pool[0] || null);
        // Il numero dei post è dinamico: manteniamo il pool reale e
        // ArgumentFeed lo distribuisce ogni cinque messaggi disponibili.
        setFeedAds(pool.length > 1 ? pool.slice(1) : pool);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const swapHeroAd = useCallback(() => {
    setHeroAd((prev) => pickAlt(poolRef.current, prev?.id) ?? prev);
  }, []);

  const swapFeedAdAt = useCallback((slotIndex) => {
    setFeedAds((prev) => {
      const next = pickAlt(poolRef.current, prev[slotIndex]?.id);
      if (!next) return prev;
      const copy = prev.slice();
      copy[slotIndex] = next;
      return copy;
    });
  }, []);

  const visibleHeroAd =
    adsEntitlementReady && !premiumNoAds
      ? heroAd
      : null;

  const visibleFeedAds =
    adsEntitlementReady && !premiumNoAds
      ? feedAds
      : [];

  return {
    heroAd: visibleHeroAd,
    feedAds: visibleFeedAds,
    swapHeroAd,
    swapFeedAdAt,
  };
}
